import { Box, BoxProps, FormControl } from '@chakra-ui/react'
import { InputValueDataTypeConfig } from '@helo/cms'
import { Deferred } from '@helo/utils'
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'

import { SelectInput } from '../../inputs/SelectInput/SelectInput'
import { TextAreaInput } from '../../inputs/TextAreaInput'
import { TextInput } from '../../inputs/TextInput/TextInput'
import { NUMBER_ONLY_CONDITION_VALUES } from '../FilterEditor/FilterEditor.parts'
import { PickListFilter } from '../PickListFilter'
import { createMockLoadOptions } from '../PickListFilter/PickListFilter.mockOptions'
import {
  CUBE_STRING_FILTER,
  CUBE_STRING_LIST_OPERATORS,
  withOptionalStringFilterMode,
} from '../stringFilterInputAdapters'

export type ConditionOption = {
  label: string
  value: string
  isArray?: boolean
  requiresValue?: boolean
}

export type FilterEditorFormMethods = {
  register: UseFormRegister<{ filterValue: string }>
  handleSubmit: UseFormHandleSubmit<{ filterValue: string }>
  setValue: UseFormSetValue<{ filterValue: string }>
  watch: UseFormWatch<{ filterValue: string }>
}

export type StringFilterEditorProps = BoxProps & {
  inputValueConfig: InputValueDataTypeConfig
  conditionOptions: ConditionOption[]
  selectedField?: ConditionOption | undefined
  updateSelectedField?: (field: ConditionOption | undefined) => void
  selectedFieldRef?: React.MutableRefObject<ConditionOption | undefined>
  formMethods?: FilterEditorFormMethods
  onUpdate?: (config: InputValueDataTypeConfig) => void
  autoFocus?: boolean
  isDisabled?: boolean
  /** Async load options for "Is Any Of" pick list; when not provided, pick list shows empty/loading */
  loadOptionsForPickList?: (
    search: string,
    limit?: number,
    notIn?: string[],
    skip?: number,
  ) => Promise<Array<{ label: string; value: string }>>
  /** When false, pick list menu should close (e.g. filter editor popover closed) */
  isFilterEditorOpen?: boolean
  /** Called after a condition is selected, so parent can advance focus to the value input */
  onConditionSelected?: () => void
  /** When set, condition dropdown menu gets this data-click-boundary so parent popover doesn't close. */
  clickBoundaryId?: string
  /** When set, overrides Chakra color mode for dark/light styling (e.g. from filter editor). */
  colorMode?: 'light' | 'dark'
  /** When true, Enter with condition menu open stops propagation so parent modal does not also handle Enter. */
  stopEnterPropagationWhenMenuOpen?: boolean
}

export type StringFilterEditorFields = {
  filterValue: string
}

/** Default dummy options so "Is Any Of" can be used and saved when no API loadOptions are provided. */
const defaultLoadOptionsForPickList = createMockLoadOptions()

export const StringFilterEditor = ({
  inputValueConfig,
  conditionOptions,
  selectedField: selectedFieldProp,
  updateSelectedField: updateSelectedFieldProp,
  selectedFieldRef: selectedFieldRefProp,
  formMethods,
  onUpdate = () => {},
  autoFocus,
  isDisabled = false,
  loadOptionsForPickList = defaultLoadOptionsForPickList,
  isFilterEditorOpen = true,
  onConditionSelected,
  clickBoundaryId,
  colorMode,
  stopEnterPropagationWhenMenuOpen,
  ...props
}: StringFilterEditorProps) => {
  if (!formMethods) {
    return null
  }

  const { register, handleSubmit, setValue, watch } = formMethods
  // Use props if provided (from FilterEditorRow), otherwise manage locally
  const [localSelectedField, setLocalSelectedField] = useState<
    ConditionOption | undefined
  >(undefined)
  const localSelectedFieldRef = useRef<ConditionOption | undefined>(undefined)
  // Preserve the last non-empty value so it can be restored when switching from no-value conditions (blank/notBlank)
  const preservedValueRef = useRef<string>('')
  const previousConditionValueRef = useRef<string | undefined>(undefined)

  const selectedField = selectedFieldProp ?? localSelectedField
  const selectedFieldRef = selectedFieldRefProp ?? localSelectedFieldRef
  const updateSelectedField =
    updateSelectedFieldProp ??
    ((field: ConditionOption | undefined) => {
      setLocalSelectedField(field)
      localSelectedFieldRef.current = field
    })

  // Find which field is currently set in the value or ofType.state.initialValue
  const currentValue = inputValueConfig.state.asValue as
    | Record<string, any>
    | undefined
  const ofTypeInitialValue = inputValueConfig.ofType?.state?.initialValue as
    | Record<string, any>
    | undefined

  // Get the current filter value based on selected field
  const getCurrentFilterValue = (): string => {
    // First check currentValue (actual filter value)
    if (currentValue && selectedField) {
      // inList uses same backend key 'in' as Is Any Of
      const valueKey =
        selectedField.value === 'inList' ? 'in' : selectedField.value
      if (currentValue[valueKey] !== undefined) {
        const val = currentValue[valueKey]
        if (Array.isArray(val)) {
          return val.join(', ')
        }
        return String(val)
      }

      // Does Not Contain: Cube `notContains` or Prisma `not.contains`
      if (selectedField.value === 'notContains') {
        if (currentValue.notContains !== undefined) {
          const val = currentValue.notContains
          return Array.isArray(val) ? val.join(', ') : String(val)
        }
        if (currentValue.not?.contains !== undefined) {
          return String(currentValue.not.contains)
        }
      }
      // Does Not Equal: Cube `notEquals` or Prisma `not.equals`
      if (
        selectedField.value === 'notEqual' &&
        currentValue.notEquals !== undefined
      ) {
        return String(currentValue.notEquals)
      }
      if (
        selectedField.value === 'notEqual' &&
        currentValue.not?.equals !== undefined &&
        currentValue.not?.equals !== null
      ) {
        return String(currentValue.not.equals)
      }
      // Equals with not.equals (legacy / alternate)
      if (
        selectedField.value === 'equals' &&
        currentValue.not?.equals !== undefined
      ) {
        if (currentValue.not.equals === null) return ''
        return String(currentValue.not.equals)
      }
    }

    // Only fall back to ofTypeInitialValue when there is genuinely no runtime state.
    // When currentValue is {} (explicitly cleared by the user), skip this — otherwise
    // setInitialVariables-propagated values (e.g. from a dashboard pre-filter) would
    // restore the original value every time the user tries to clear the input.
    if (!currentValue && ofTypeInitialValue && selectedField) {
      const valueKey =
        selectedField.value === 'inList' ? 'in' : selectedField.value
      const initialVal = ofTypeInitialValue[valueKey]
      if (initialVal !== undefined) {
        if (Array.isArray(initialVal)) {
          return initialVal.join(', ')
        }
        return String(initialVal)
      }
    }

    return ''
  }

  const { ref: formRef, ...filterValueRegistration } = register('filterValue')

  // Watch for changes and update on blur or Enter key
  const watchedValue = watch('filterValue')

  // Sync form value from filter state when condition changes (to avoid placeholder flicker). For In List we do not sync on every state change so the user can type commas and spaces without them being overwritten by the parsed array.
  useLayoutEffect(() => {
    const conditionValue = selectedField?.value
    const conditionJustChanged =
      previousConditionValueRef.current !== conditionValue
    previousConditionValueRef.current = conditionValue

    const isInListTextInput = selectedField?.value === 'inList'
    if (isInListTextInput && !conditionJustChanged) {
      return
    }

    const fromState = getCurrentFilterValue()
    const valueToSet =
      fromState ||
      (conditionJustChanged &&
        selectedField?.requiresValue !== false &&
        preservedValueRef.current) ||
      ''
    setValue('filterValue', valueToSet)
  }, [
    selectedField?.value,
    inputValueConfig.state.asValue,
    ofTypeInitialValue,
    setValue,
  ])

  const filterTypeName = inputValueConfig.ofType?.typeName
  const isCubeStringFilter = filterTypeName === CUBE_STRING_FILTER

  const withOptionalMode = (value: Record<string, any>) =>
    withOptionalStringFilterMode(value, filterTypeName) as Record<string, any>

  const buildUpdatedConfigFromValue = (filterValue: string) => {
    const currentSelectedField = selectedFieldRef.current
    if (!currentSelectedField) return

    const rawValue = filterValue ?? ''
    const isEmpty = rawValue.trim() === ''
    let newValue: Record<string, any> = {}

    if (currentSelectedField.value === 'equals') {
      newValue.equals = isEmpty ? '' : rawValue
      if (!isEmpty) {
        newValue = withOptionalMode(newValue)
      }
    } else if (currentSelectedField.value === 'blank') {
      newValue = isCubeStringFilter ? { set: false } : { equals: null }
    } else if (currentSelectedField.value === 'notBlank') {
      newValue = isCubeStringFilter ? { set: true } : { not: { equals: null } }
    } else if (currentSelectedField.value === 'notContains') {
      if (!isEmpty) {
        newValue = withOptionalMode(
          isCubeStringFilter
            ? { notContains: [rawValue] }
            : { not: { contains: rawValue } },
        )
      }
    } else if (currentSelectedField.value === 'notEqual') {
      if (!isEmpty) {
        newValue = withOptionalMode(
          isCubeStringFilter
            ? { notEquals: rawValue }
            : { not: { equals: rawValue } },
        )
      }
    } else if (currentSelectedField.value === 'inList') {
      // In List: comma-separated multi-value, sent under backend key 'in'
      const arrayValues = rawValue
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
      if (arrayValues.length > 0) {
        newValue = withOptionalMode({ in: arrayValues })
      }
    } else if (
      isCubeStringFilter &&
      CUBE_STRING_LIST_OPERATORS.has(currentSelectedField.value)
    ) {
      // Cube list operators (contains/startsWith/endsWith) take a single value
      // wrapped as a one-element array; do not comma-split.
      if (!isEmpty) {
        newValue = withOptionalMode({
          [currentSelectedField.value]: [rawValue],
        })
      }
    } else {
      if (!isEmpty) {
        newValue = withOptionalMode({
          [currentSelectedField.value]: rawValue,
        })
      }
    }

    return {
      ...inputValueConfig,
      state: Deferred.merge({ asValue: newValue })(
        inputValueConfig.state ?? {},
      ),
    }
  }

  const onSubmit = ({ filterValue }: StringFilterEditorFields) => {
    const updatedConfig = buildUpdatedConfigFromValue(filterValue ?? '')
    if (updatedConfig) onUpdate(updatedConfig)
  }

  const handleValueChange = (filterValue: string) => {
    const updatedConfig = buildUpdatedConfigFromValue(filterValue)
    if (updatedConfig) onUpdate(updatedConfig)
  }

  // Only "In List" uses the multi-value textarea. Cube string operators like
  // Contains/Starts With/Ends With are LIST types in the schema (isArray), but
  // are single-value inputs; their value is wrapped as a one-element array for
  // the cube backend in the value-building logic below.
  const isMultiValue = selectedField?.value === 'inList'
  const isAnyOf = selectedField?.value === 'in'
  const isNoValueCondition = selectedField?.requiresValue === false // Is Blank, Is Not Blank

  const pickListValue = useMemo(() => {
    if (!currentValue?.in || !Array.isArray(currentValue.in)) return []
    return currentValue.in.filter(
      (v: unknown): v is string => typeof v === 'string' && v !== '',
    )
  }, [currentValue?.in])

  const handlePickListChange = (values: string[]) => {
    const newValue: Record<string, any> =
      values.length > 0 ? withOptionalMode({ in: values }) : { in: undefined }
    const updatedConfig = {
      ...inputValueConfig,
      state: Deferred.merge({ asValue: newValue })(
        inputValueConfig.state ?? {},
      ),
    }
    onUpdate(updatedConfig)
  }

  const stringConditionOptions = useMemo(
    () =>
      conditionOptions.filter(
        opt => !NUMBER_ONLY_CONDITION_VALUES.has(opt.value),
      ),
    [conditionOptions],
  )

  return (
    <>
      <Box data-testid="string-filter-condition-select" flex="1" minWidth={0}>
        <SelectInput
          variant="filterCondition"
          options={stringConditionOptions}
          placeholder="Search or select condition"
          isDisabled={isDisabled}
          filterMode="substring"
          menuBoundaryId={clickBoundaryId}
          stopEnterPropagationWhenMenuOpen={stopEnterPropagationWhenMenuOpen}
          menuIsOpen={isFilterEditorOpen ? undefined : false}
          onChange={(option: any) => {
            const selected = conditionOptions.find(
              opt => opt.value === option.value,
            )
            if (selected) {
              // Preserve the current value before switching (only from value-requiring conditions); keep raw so spaces are preserved
              if (selectedField?.requiresValue !== false) {
                const currentForm = watchedValue || ''
                if (currentForm) {
                  preservedValueRef.current = currentForm
                }
              }
              updateSelectedField(selected)
              onConditionSelected?.()
              // Don't clear the field value - keep it when changing condition
              // Defer one microtask so form/watch state can settle without setTimeout
              queueMicrotask(() => {
                // Get the current form value (raw), falling back to preserved value when empty (e.g. after blank/notBlank)
                const currentFormValue = watchedValue || ''
                const rawFormValue =
                  currentFormValue || preservedValueRef.current
                const isEmptyForm = rawFormValue.trim() === ''
                const arrayValuesFromForm = rawFormValue
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
                let newValue: Record<string, any> = {}

                if (selected.value === 'equals') {
                  newValue.equals = isEmptyForm ? '' : rawFormValue
                  if (!isEmptyForm) {
                    newValue = withOptionalMode(newValue)
                  }
                } else if (selected.value === 'in') {
                  // Is Any Of: restore from existing .in array, or parse comma-separated form value (e.g. after switching from Contains) so the pick list shows prior selections
                  const existing = currentValue?.in
                  let arr: string[] = Array.isArray(existing)
                    ? existing.filter(
                        (v: unknown): v is string =>
                          typeof v === 'string' && v !== '',
                      )
                    : []
                  if (arr.length === 0 && arrayValuesFromForm.length > 0) {
                    arr = arrayValuesFromForm
                  }
                  if (arr.length > 0) {
                    newValue = withOptionalMode({ in: arr })
                  }
                } else if (selected.value === 'inList') {
                  // In List: restore from existing .in or parse comma-separated form value when switching from another condition
                  const existing = currentValue?.in
                  let arr: string[] = Array.isArray(existing)
                    ? existing.filter(
                        (v: unknown): v is string =>
                          typeof v === 'string' && v !== '',
                      )
                    : []
                  if (arr.length === 0 && arrayValuesFromForm.length > 0) {
                    arr = arrayValuesFromForm
                  }
                  if (arr.length > 0) {
                    newValue = withOptionalMode({ in: arr })
                  }
                } else if (selected.value === 'blank') {
                  newValue = isCubeStringFilter
                    ? { set: false }
                    : { equals: null }
                } else if (selected.value === 'notBlank') {
                  newValue = isCubeStringFilter
                    ? { set: true }
                    : { not: { equals: null } }
                } else if (selected.value === 'notContains') {
                  if (!isEmptyForm) {
                    newValue = withOptionalMode(
                      isCubeStringFilter
                        ? { notContains: [rawFormValue] }
                        : { not: { contains: rawFormValue } },
                    )
                  }
                } else if (selected.value === 'notEqual') {
                  if (!isEmptyForm) {
                    newValue = withOptionalMode(
                      isCubeStringFilter
                        ? { notEquals: rawFormValue }
                        : { not: { equals: rawFormValue } },
                    )
                  }
                } else if (
                  isCubeStringFilter &&
                  CUBE_STRING_LIST_OPERATORS.has(selected.value)
                ) {
                  // Cube list operators (contains/startsWith/endsWith) take a
                  // single value wrapped as a one-element array.
                  if (!isEmptyForm) {
                    newValue = withOptionalMode({
                      [selected.value]: [rawFormValue],
                    })
                  }
                } else {
                  // Always set the condition key (even empty) so stored value reflects selection and sync effect doesn't clear selectedField
                  newValue[selected.value] = isEmptyForm ? '' : rawFormValue
                  if (!isEmptyForm) {
                    newValue = withOptionalMode(newValue)
                  }
                }

                const updatedConfig = {
                  ...inputValueConfig,
                  state: Deferred.merge({ asValue: newValue })(
                    inputValueConfig.state ?? {},
                  ),
                }
                onUpdate(updatedConfig)
              })
            }
          }}
          value={selectedField}
          flex="1"
        />
      </Box>
      {conditionOptions.length > 0 && (
        <FormControl flex="1" isDisabled={isDisabled}>
          {!selectedField ? (
            <TextInput placeholder="Search or input value" disabled flex="1" />
          ) : isNoValueCondition ? (
            <TextInput placeholder="No value needed" disabled flex="1" />
          ) : isAnyOf ? (
            <Box flex="1" minWidth={0}>
              <PickListFilter
                value={pickListValue}
                onChange={handlePickListChange}
                loadOptions={loadOptionsForPickList}
                placeholder="Search or select value"
                isDisabled={isDisabled}
                isFilterEditorOpen={isFilterEditorOpen}
                colorMode={colorMode}
              />
            </Box>
          ) : isMultiValue ? (
            <TextAreaInput
              data-testid="string-filter-inlist-input"
              ref={formRef}
              {...filterValueRegistration}
              disabled={isDisabled}
              onChange={e => {
                filterValueRegistration.onChange(e)
                handleValueChange(e.target.value)
              }}
              onBlur={() => {
                handleSubmit(onSubmit)()
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  setValue('filterValue', e.currentTarget.value.trim())
                  handleSubmit(onSubmit)()
                }
              }}
              autoFocus={autoFocus}
              placeholder="Enter Value1, Value2, Value3"
            />
          ) : (
            <TextInput
              data-testid="string-filter-value-input"
              ref={formRef}
              {...filterValueRegistration}
              autoFocus={autoFocus}
              placeholder="Enter value"
              disabled={isDisabled}
              onChange={e => {
                filterValueRegistration.onChange(e)
                handleValueChange(e.target.value)
              }}
              onBlur={() => {
                handleSubmit(onSubmit)()
              }}
            />
          )}
        </FormControl>
      )}
    </>
  )
}

StringFilterEditor.displayName = 'StringFilterEditor'
